import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/Button";
import { ChipRow } from "../../../ui/ChipRow";
import { Input } from "../../../ui/Input";
import { Textarea } from "../../../ui/Textarea";
import { RubricForm } from "../RubricForm";
import { displayToast } from "../../../app/AppToast";
import { apiClient, useApiQuery } from "../../../../services/api";
import {
  closeAnnotation,
  popAnnotationView,
  revalidateSubjectAnnotations,
  type AnnotationTarget,
  type SubjectAnnotation,
} from "../../../../domains/gpt/stores/annotationSheetStore";

type ToolChoice =
  | "vera_health"
  | "open_evidence"
  | "chatgpt"
  | "perplexity"
  | "uptodate"
  | "consensus"
  | "other";

/**
 * Which record the form is bound to, alongside its fields.
 *
 * `editingId` and `confirmingId` are form state, not component state — `useState`
 * is banned in business components, and these genuinely describe the form: which
 * comparison it is editing, and which one has its delete armed.
 */
type CaptureForm = {
  editingId: string | null;
  confirmingId: string | null;
  tool: ToolChoice | null;
  otherLabel: string;
  modelLabel: string;
  answer: string;
};

const BLANK: CaptureForm = {
  editingId: null,
  confirmingId: null,
  tool: null,
  otherLabel: "",
  modelLabel: "",
  answer: "",
};

// Brand names, so they are not translated — only the escape hatch is.
const NAMED_TOOLS = {
  vera_health: "Vera Health",
  open_evidence: "OpenEvidence",
  chatgpt: "ChatGPT",
  perplexity: "Perplexity",
  uptodate: "UpToDate",
  consensus: "Consensus",
} as const satisfies Record<Exclude<ToolChoice, "other">, string>;

function toolLabel(annotation: SubjectAnnotation, fallback: string): string {
  const tool = annotation.tool;
  if (tool === null) return fallback;
  if (tool.kind === "other") return tool.label === "" ? fallback : tool.label;
  return NAMED_TOOLS[tool.name];
}

/**
 * Phase 2: competitor answers, scored on the SAME rubric — a fresh verdict each
 * time, never a copy of ours. The gap between the two is the whole point, so
 * applying phase 1's ratings to a pasted answer would store a measurement nobody
 * made.
 *
 * Reachable only after phase 1, and the API refuses it otherwise: seeing a
 * competitor answer before judging ours contaminates the judgement, and the corpus
 * cannot tell a contaminated verdict from a clean one. The order is not a
 * convention here, it is a 409. The VERDICT, by contrast, stays revisable — which
 * is why the banner offers the way back rather than claiming a lock.
 *
 * The list reads from the conversation query, not from the sheet's target: the
 * target is a snapshot taken when the sheet opened, so a comparison added or
 * removed a second ago would be missing from it.
 */
export function CompareAnswerView({
  target,
}: {
  target: AnnotationTarget;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { watch, setValue, register, reset } = useForm<CaptureForm>({
    defaultValues: BLANK,
  });
  const capture = watch();

  const subject = target.subject;
  const conversationQuery = useApiQuery(
    "/webapp/private/conversations/:id",
    subject.engine === "legacy"
      ? { param: { id: subject.conversationId } }
      : false
  );
  const assistantQuery = useApiQuery(
    "/assistant/conversations/:conversationId/annotations",
    subject.engine === "assistant"
      ? { param: { conversationId: subject.conversationId } }
      : false
  );
  const annotations: SubjectAnnotation[] =
    subject.engine === "legacy"
      ? conversationQuery.state === "success"
        ? conversationQuery.body.myAnnotations.filter(
            (annotation) => annotation.turnIndex === subject.turnIndex
          )
        : []
      : assistantQuery.state === "success"
        ? assistantQuery.body.annotations.filter(
            (annotation) => annotation.runId === subject.runId
          )
        : [];
  const comparisons = annotations.filter(
    (annotation) => annotation.origin === "external"
  );
  const editing =
    capture.editingId === null
      ? null
      : comparisons.find((comparison) => comparison.id === capture.editingId);

  // `other` carries its free text and a named tool must not — the same
  // equivalence the database CHECK enforces, so the wrong arm cannot be sent.
  const tool =
    capture.tool === null
      ? null
      : capture.tool === "other"
        ? capture.otherLabel.trim() === ""
          ? null
          : ({ kind: "other", label: capture.otherLabel.trim() } as const)
        : ({ kind: "named", name: capture.tool } as const);

  const remove = async (annotationId: string): Promise<void> => {
    try {
      if (subject.engine === "legacy")
        await apiClient.delete(
          "/webapp/private/conversations/:id/annotations/:annotationId",
          { param: { id: subject.conversationId, annotationId } }
        );
      else
        await apiClient.delete(
          "/assistant/conversations/:conversationId/annotations/:annotationId",
          { param: { conversationId: subject.conversationId, annotationId } }
        );
      await revalidateSubjectAnnotations(subject);
      reset(BLANK);
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };

  return (
    <RubricForm
      // Remounted per record, so the rubric reloads its defaults when the form
      // switches between "a new comparison" and "editing that one".
      key={capture.editingId === null ? "new" : capture.editingId}
      initial={
        editing === undefined || editing === null ? null : editing.verdict
      }
      submitLabel={
        capture.editingId === null ? t("Add this answer") : t("Save changes")
      }
      secondaryLabel={capture.editingId === null ? t("Finish") : t("Cancel")}
      onSecondary={
        capture.editingId === null
          ? closeAnnotation
          : () => {
              reset(BLANK);
            }
      }
      onSubmit={async (verdict) => {
        if (tool === null) return;
        const body = {
          tool,
          modelLabel:
            capture.modelLabel.trim() === "" ? null : capture.modelLabel.trim(),
          capturedAt: new Date().toISOString(),
          answer: capture.answer.trim(),
          verdict,
        };
        try {
          const editingId = capture.editingId;
          if (subject.engine === "legacy") {
            if (editingId === null)
              await apiClient.post(
                "/webapp/private/conversations/:id/annotations/external",
                {
                  param: { id: subject.conversationId },
                  json: { turnIndex: subject.turnIndex, ...body },
                }
              );
            else
              await apiClient.put(
                "/webapp/private/conversations/:id/annotations/:annotationId",
                {
                  param: {
                    id: subject.conversationId,
                    annotationId: editingId,
                  },
                  json: body,
                }
              );
          } else if (editingId === null)
            await apiClient.post(
              "/assistant/conversations/:conversationId/annotations/external",
              {
                param: { conversationId: subject.conversationId },
                json: { runId: subject.runId, ...body },
              }
            );
          else
            await apiClient.put(
              "/assistant/conversations/:conversationId/annotations/:annotationId",
              {
                param: {
                  conversationId: subject.conversationId,
                  annotationId: editingId,
                },
                json: body,
              }
            );
          await revalidateSubjectAnnotations(subject);
          // Stay in the view with an empty form: comparing against a second tool
          // is the normal case, and closing here used to force a trip back
          // through phase 1 — which filed a revision of a rating nobody changed.
          reset(BLANK);
        } catch {
          displayToast(t("Something went wrong. Please try again."), "error");
        }
      }}
    >
      {/* What is locked is the ORDER, never the verdict: phase 2 is refused
            before phase 1 (a 409), because seeing a competitor answer first
            contaminates the judgement. The verdict itself stays revisable — so the
            banner says "saved", not "locked", and carries the way back. Saying
            "locked" is what made this read as a wall. */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-ds-mint-100 px-4 py-3">
        <span className="font-dm-sans text-ui-note font-medium text-ds-mint-800">
          {t("Your rating is saved. You can still change it.")}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={popAnnotationView}
        >
          {t("Change my rating")}
        </Button>
      </div>

      {comparisons.length === 0 ? null : (
        <div className="flex flex-col gap-2">
          <span className="font-dm-sans text-ui-body font-semibold text-ds-moss-800">
            {t("Answers you already compared")}
          </span>
          {comparisons.map((comparison) => (
            <div
              key={comparison.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-ds-slate-200 px-4 py-3"
            >
              <span className="font-dm-sans text-ui-body text-ds-black">
                {toolLabel(comparison, t("Other"))}
              </span>
              {capture.confirmingId === comparison.id ? (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setValue("confirmingId", null);
                    }}
                  >
                    {t("Cancel")}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      void remove(comparison.id);
                    }}
                  >
                    {t("Delete for good")}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // `reset` rather than field-by-field: it rebinds the whole
                      // form to this record in one go, and the RubricForm remount
                      // keyed on `editingId` reloads the rubric with it.
                      reset({
                        ...BLANK,
                        editingId: comparison.id,
                        tool:
                          comparison.tool === null
                            ? null
                            : comparison.tool.kind === "other"
                              ? "other"
                              : comparison.tool.name,
                        otherLabel:
                          comparison.tool === null ||
                          comparison.tool.kind !== "other"
                            ? ""
                            : comparison.tool.label,
                        modelLabel:
                          comparison.modelLabel === null
                            ? ""
                            : comparison.modelLabel,
                        answer:
                          comparison.answer === null ? "" : comparison.answer,
                      });
                    }}
                  >
                    {t("Edit")}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setValue("confirmingId", comparison.id);
                    }}
                  >
                    {t("Delete")}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="font-dm-sans text-ui-body font-normal text-ds-slate-600">
        {capture.editingId === null
          ? t("Did you ask this question anywhere else?")
          : t("Editing this comparison")}
      </p>
      <ChipRow
        value={capture.tool}
        onChange={(value) => {
          setValue("tool", value);
        }}
        label={t("Which tool")}
        items={[
          { value: "vera_health", label: NAMED_TOOLS.vera_health },
          { value: "open_evidence", label: NAMED_TOOLS.open_evidence },
          { value: "chatgpt", label: NAMED_TOOLS.chatgpt },
          { value: "perplexity", label: NAMED_TOOLS.perplexity },
          { value: "uptodate", label: NAMED_TOOLS.uptodate },
          { value: "consensus", label: NAMED_TOOLS.consensus },
          { value: "other", label: t("Other") },
        ]}
      />
      {capture.tool === "other" && (
        <Input
          placeholder={t("Which tool was it?")}
          {...register("otherLabel")}
        />
      )}
      <Textarea
        rows={6}
        placeholder={t("Paste the answer you got here")}
        {...register("answer")}
      />
      <Input
        placeholder={t("Model version, if you know it")}
        {...register("modelLabel")}
      />
      {tool !== null && capture.answer.trim() !== "" ? null : (
        <p className="font-dm-sans text-ui-note font-normal text-ds-slate-600">
          {t("Pick a tool and paste its answer, then score it below.")}
        </p>
      )}
    </RubricForm>
  );
}
